/**
 * D6 -- the IBS page had no strip.
 *
 * The finding page tells its owner how long an incident has been going
 * on and when it becomes a breach. His own service's page -- the one he
 * reaches from the alert, from My Work, from the register -- said
 * nothing about it at all: he had to notice a number in a chip row and
 * go looking. The service page is where an owner starts, so it is where
 * the answer belongs.
 *
 * Only for someone the ESTATE says owns this IBS, and only while
 * something is open. A closed incident wants the record, not a running
 * clock; a reader who does not own the service is not the person this
 * sentence is written for.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { formatDateTime, formatTime } from "shared/time/formatDate";
import { useToleranceState } from "shared/tolerance/useToleranceState";
import { useFindings } from "twin/api/hooks";
import { breachAt, elapsedSince } from "twin/finding/OwnerIncidentStrip";
import { useMyEstate } from "twin/home/useMyEstate";

const SEVERITY_RANK: Record<string, number> = {
  critical: 3,
  high: 2,
  medium: 1,
  low: 0,
};

const ALL_SEVERITIES = ["critical", "high", "medium", "low"] as const;

export function OwnerIBSIncidentStrip({ ibsId }: { ibsId: string | undefined }) {
  const { t, i18n } = useTranslation();
  const estate = useMyEstate();
  const tolerance = useToleranceState();
  const owned = (estate.data?.ibses ?? []).find((i) => i.id === ibsId);
  const findings = useFindings({
    entity_kind: "ibs_service",
    entity_id: ibsId ?? "",
    status: "open",
    /* Round 16 H29's drive found this: the list endpoint defaults to
       critical and high, and the strip asked for nothing, so an owner
       whose service had only a medium or low incident open got no strip
       at all -- while the ranking above counts all four. Ask for all
       four; the worst still drives the sentence. */
    severities: ALL_SEVERITIES,
    enabled: Boolean(ibsId) && owned !== undefined,
  });

  if (!owned) return null;
  const open = findings.data ?? [];
  if (open.length === 0) return null;

  // The worst one drives the sentence, and the oldest of that severity
  // drives the clock: the breach that arrives first is the one worth
  // putting at the top of the page.
  const worst = [...open].sort((a, b) => {
    const rank = (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0);
    return rank !== 0
      ? rank
      : new Date(a.raised_at).getTime() - new Date(b.raised_at).getTime();
  })[0]!;

  const state = (tolerance.data?.rows ?? []).find((r) => r.ibs_service_id === ibsId);
  const hours = state?.hours ?? null;
  const breach = breachAt(worst.raised_at, hours);
  /* Round 16 G10 -- the strip said "breach at 21:03" above a finding
     whose own page said "Breaching now", and on a finding 129 hours old
     "breach at 11:13" with no day. A live breach is the present tense;
     a projection carries its date unless it is today, and one already
     behind us says so. */
  const live = open.find(
    (f) =>
      (f.external_ref ?? "").startsWith("tolerance-breach:") &&
      typeof f.payload?.["projected_recovery_hours"] === "number",
  );
  const now = new Date();
  const toleranceLine = (): string => {
    if (live) {
      return t("modules.twin.ibsIncident.breachingNow", {
        projected: live.payload["projected_recovery_hours"] as number,
        hours: (live.payload["impact_tolerance_hours"] as number | undefined) ?? hours,
      });
    }
    if (hours === null) return t("modules.twin.ibsIncident.toleranceNone");
    if (breach && breach.getTime() <= now.getTime()) {
      return t("modules.twin.ibsIncident.toleranceBreached", {
        hours,
        breach: formatDateTime(breach.toISOString(), i18n.language),
      });
    }
    const sameDay = breach !== null && breach.toDateString() === now.toDateString();
    return t("modules.twin.ibsIncident.tolerance", {
      hours,
      breach: sameDay
        ? formatTime(breach.toISOString(), i18n.language)
        : formatDateTime(breach?.toISOString() ?? null, i18n.language),
    });
  };

  return (
    <section
      data-testid="owner-ibs-incident-strip"
      className="rounded-lg border border-rag-redEdge bg-rag-redBg p-4"
    >
      <p data-testid="owner-ibs-incident-headline" className="text-base text-slate-900">
        {t("modules.twin.ibsIncident.headline", {
          count: open.length,
          name: owned.name,
          severity: worst.severity,
          elapsed: elapsedSince(worst.raised_at, new Date()),
        })}
      </p>
      <p
        data-testid="owner-ibs-incident-tolerance"
        className="mt-1 text-sm text-slate-700"
      >
        {toleranceLine()}
      </p>
      <p className="mt-2 text-sm">
        <Link
          to={`/twin/findings/${worst.id}`}
          data-testid="owner-ibs-incident-open"
          /* Round 16 H29 -- the strip's one action measured 173 by 24
             pixels on a phone; a 44-pixel target, still a link. */
          className="inline-flex min-h-[44px] items-center text-brand-brassDark hover:underline"
        >
          {t("modules.twin.ibsIncident.open", { title: worst.title })}
        </Link>
      </p>
    </section>
  );
}
